import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  JwtRefresher,
  Pagination,
  PaginationContainer,
  SearchBox,
  SearchBoxContainer,
} from '@white-nl/instantsearch-components';
import { history } from 'instantsearch.js/es/lib/routers';
/* eslint-disable-next-line import/no-extraneous-dependencies */
import { Configure, InstantSearch, useInstantSearch } from 'react-instantsearch';
/* eslint-disable-next-line import/no-extraneous-dependencies */
import qs from 'qs';
/* eslint-disable-next-line import/no-extraneous-dependencies */
import SearchkitClient from '@searchkit/instantsearch-client';
import Hits from '../results/Search';
import { useGetSearchTranslatesQuery } from '../../redux-modules/service';

export default function SearchOverview({
  indexName,
  publicUrl,
  refreshUrl,
  locale,
  siteId,
  userGroups,
  loadingText,
  baseUrl,
}) {
  const [
    currentSearchClient,
    setCurrentSearchClient,
  ] = useState(null);

  const {
    data: translatesData,
    isFetching: isTranslatesDataFetching,
  } = useGetSearchTranslatesQuery();

  if (isTranslatesDataFetching || translatesData.translates.length === 0) {
    return loadingText;
  }

  let defaultFilters = `siteId:${siteId}`;

  let qUserGroups = userGroups.split(',');
  qUserGroups = qUserGroups.join(' OR userGroups:');
  defaultFilters = `${defaultFilters} AND (${qUserGroups && `userGroups:${qUserGroups} OR `}userGroups:all)`;

  const additionalData = {
    locale,
    indexName,
    currentSite: siteId,
    translates: translatesData.translates,
  };

  const {
    search,
  } = translatesData.translates;

  const pathAttribute = '';
  const addToPath = true;
  const basePath = new URL(baseUrl).pathname.replace(/^\s*\/*\s*|\s*\/*\s*$/gm, '');
  const siteUrl = '';
  const addTrailingSlashesToUrls = false;

  const query = {
    queryStringToFilters: (queryString, mapping = {}) => {
      const result = {};

      const q = qs.parse(queryString);

      const filters = (q);

      Object.keys(filters).forEach((attribute) => {
        const key = mapping[attribute] || attribute;
        const values = filters[attribute] || [];
        result[key] = (Array.isArray(values) ? values : [values])
          .filter(Boolean)
          .map(query.decodeURIComponent);
      });

      return result;
    },
    decodeURIComponent(encodedURIComponent) {
      try {
        return decodeURIComponent(encodedURIComponent);
      } catch (_) {
        /**
         * By default, percentage character (%) is not encoded.
         * Following function should find single percentage character (%),
         * not followed by two hexadecimal digits and encode it.
         */
        return decodeURIComponent(encodedURIComponent.replace(/%(?![0-9a-fA-F]{2})/g, '%25'));
      }
    },
  };

  const jwtRefresher = (
    <JwtRefresher
      cookieName="elasticsearch_token"
      checkIntervalSeconds={5}
      refreshTimeBeforeExpirySeconds={30}
      refreshIframeUrl={refreshUrl}
      onRefreshed={(token) => {
        const config = {
          url: publicUrl,
          headers: {
            authorization: `Bearer ${token}`,
          },
        };
        const searchKitClient = Object.assign(SearchkitClient(config), {
          transporter: {
            headers: {
              'x-algolia-application-id': 'NULL',
              'x-algolia-api-key': 'NULL',
            },
          },
        });
        setCurrentSearchClient(searchKitClient);
      }}
    />
  );

  if (!currentSearchClient) {
    return jwtRefresher;
  }

  const hitsPerPage = 12;
  let currentPagination = 0;

  function RenderState() {
    const { indexRenderState } = useInstantSearch();

    if (indexRenderState && indexRenderState.searchBox) {
      const searchQuery = indexRenderState.searchBox.query;
      document.getElementById('search-title').innerHTML = searchQuery
        ? Craft.t(search, 'Search results for "{query}"', { query: searchQuery })
        : Craft.t(search, 'All search results');
    }
    if (indexRenderState && indexRenderState.pagination) {
      if (indexRenderState.pagination.currentRefinement !== currentPagination) {
        currentPagination = indexRenderState.pagination.currentRefinement;
        setTimeout(() => {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }, 50);
      }
    }

    return null;
  }

  return (
    <>
      {jwtRefresher}
      <InstantSearch
        indexName={indexName}
        searchClient={currentSearchClient}
        future={{
          preserveSharedStateOnUnmount: true,
        }}
        routing={{
          router: history({
            createURL({ qsModule, location, routeState }) {
              const { origin, hash } = location;

              const queryParameters = {};
              Object.keys(routeState).forEach((key) => {
                const value = routeState[key];
                if (value) {
                  queryParameters[key] = value;
                }
              });

              let slug = '';
              if (pathAttribute && queryParameters[pathAttribute]) {
                slug = Array.isArray(queryParameters[pathAttribute])
                  ? queryParameters[pathAttribute].join('/')
                  : queryParameters[pathAttribute];
                slug = addToPath && basePath ? `/${basePath}/${slug}` : `/${slug}`;

                delete queryParameters[pathAttribute];
              }

              const queryString = qsModule.stringify(queryParameters, {
                addQueryPrefix: true,
                arrayFormat: 'repeat',
              });

              if (slug) {
                return `${(siteUrl || origin)}${slug}${addTrailingSlashesToUrls ? '/' : ''}${queryString}${hash}`;
              }

              if (basePath) {
                return `${(siteUrl || origin)}/${basePath}${addTrailingSlashesToUrls ? '/' : ''}${queryString}${hash}`;
              }

              return `${(siteUrl || origin)}${addTrailingSlashesToUrls ? '/' : ''}${queryString}${hash}`;
            },
            parseURL({ location }) {
              const searchQ = location.search;

              const values = query.queryStringToFilters(searchQ.slice(1), {});
              const result = {};

              result.query = values.query;
              result.page = values.page;

              return result;
            },
            writeDelay: 0,
          }),
          stateMapping: {
            stateToRoute(uiState) {
              const indexUiState = uiState[indexName];
              const result = {};

              result.query = indexUiState?.query;
              result.page = indexUiState?.page;
              return result;
            },
            routeToState(routeState) {
              const result = {
                query: routeState.query
                && Array.isArray(routeState.query)
                && routeState.query.length > 0 ? routeState.query[0] : undefined,
                page: routeState.page
                && Array.isArray(routeState.page)
                && routeState.page.length > 0 ? routeState.page[0] : undefined,
              };

              return {
                [indexName]: result,
              };
            },
          },
        }}
      >
        <Configure
          hitsPerPage={hitsPerPage}
          filters={defaultFilters}
        />

        <SearchBoxContainer
          additionalData={additionalData}
          searchBoxSettings={{
            placeholder: Craft.t(search, 'What are you looking for?'),
            classNames: {
              root: 'd-none',
            },
          }}
          searchBoxComponent={SearchBox}
        />
        <RenderState />

        <div className="section-head mt-5">
          <div className="row align-items-center">
            <div className="col-12">
              <h2 id="search-title">{Craft.t(search, 'All search results')}</h2>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="row g-4">
              <Hits additionalData={additionalData} />
              <PaginationContainer
                paginationComponent={Pagination}
                paginationSettings={{
                  classNames: {
                    list: 'pagination',
                    item: 'page-item',
                    selectedItem: 'active',
                    disabledItem: 'disabled',
                    link: 'page-link',
                  },
                  enableCanonicalUpdates: true,
                  showFirst: false,
                  showLast: false,
                }}
                additionalData={additionalData}
              />
            </div>
          </div>

        </div>
      </InstantSearch>
    </>
  );
}

SearchOverview.propTypes = {
  indexName: PropTypes.string,
  publicUrl: PropTypes.string,
  refreshUrl: PropTypes.string,
  baseUrl: PropTypes.string,
  locale: PropTypes.string,
  loadingText: PropTypes.string,
  siteId: PropTypes.number,
  userGroups: PropTypes.string,
};
